import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { notification } from 'antd';
import { CountdownContext } from '@/CountdownContextProvider';
import { thunkFetchHomescreenWidgets, setWidgets } from '@/redux/actions';
import WidgetBuilder from './WidgetBuilder';
import WidgetBuilderHeader from './WidgetBuilderHeader/WidgetBuilderHeader';
import './WidgetBuilder.scss';

const createWidgetRoute = 'widgets/POST';
const updateWidgetRoute = 'widgets/:widgetID/PATCH';
const deleteWidgetRoute = 'widgets/:widgetID/DELETE';

const WidgetBuilderContainer = props => {
	const { event, auth0Token, socket, fetchHomescreenWidgets, widgets, setWidgets, page } = props;
	const { countdownRef, setCountdown } = useContext(CountdownContext);
	const [widgetToUpdate, setWidgetToUpdate] = useState({});
	const [showUpdateOptions, setShowUpdateOptions] = useState(false);
	const [theme, setTheme] = useState({});
	const [hasChanges, setHasChanges] = useState(false);
	const [preview, setPreview] = useState('tablet');
	const [pageName, setPageName] = useState('');

	useEffect(() => {
		window.Beacon('suggest', ['65ce31a90ba13506ce3ba371']);
		if (page) {
			setWidgets(page.widgets || []);
			setTheme(page.theme || {});
			setPageName(page.name);
		} else {
			setTheme(event.homescreen_theme);
			fetchHomescreenWidgets(event.id, 'useEffect.WidgetBuilderContainer.index');
		}

		return () => {
			window.Beacon('suggest', []);
			setWidgets([]);
		};
	}, [page]);

	useEffect(() => {
		if (!countdownRef.current) handleWidgetOrder();
	}, [widgets]);

	const handleWidgetOrder = () => {
		const widgetOrder = widgets.map(widget => widget.id);
		// resets to inital val to avoid retriggering useEffect
		setCountdown(-1);
		setHasChanges(false);
		if (!page)
			socket.emit(
				'event/:eventID/PATCH',
				{
					id: event.id,
					homescreen_widget_order: JSON.stringify(widgetOrder),
					homescreen_theme: JSON.stringify(theme),
				},
				auth0Token,
				null,
				'widgetHomescreen'
			);
		else {
			socket.emit(
				'event/:eventID/pages/:pageID/PATCH',
				{
					page: {
						id: page.id,
						event_id: event.id,
						name: pageName,
						theme: JSON.stringify(theme),
						widget_order: JSON.stringify(widgetOrder),
					},
				},
				auth0Token
			);
		}
	};

	const handleSubmit = () => {
		const placeholdersNum = widgets.filter(
			widget => !widget.id || widget.toDelete || widget.toUpdate
		).length;
		setCountdown(placeholdersNum);
		if (!placeholdersNum) handleWidgetOrder();
		else {
			widgets.forEach((widget, idx) => {
				let index = idx;
				const newWidget = {
					type: widget.type,
					props: JSON.stringify(widget.props),
					id: widget.id,
					event_id: event.id,
				};
				const groupIDs = widget.groups || [];
				if (widget.toDelete) {
					index--; // correct the index for passing into create widget
					socket.emit(deleteWidgetRoute, widget.id, event.id, widget.type, auth0Token);
				} else if (widget.toUpdate)
					socket.emit(updateWidgetRoute, newWidget, event.id, groupIDs, auth0Token);
				else if (!widget.id && widget.type)
					socket.emit(createWidgetRoute, newWidget, index, event.id, groupIDs, auth0Token);
				else return;
			});
		}
	};

	const handleWidgetDelete = widget => {
		const indexToRemove = widgets.indexOf(widget);
		if (widget.id) {
			widget.toDelete = true;
			setWidgets(
				[...widgets.slice(0, indexToRemove), widget, ...widgets.slice(indexToRemove + 1)],
				'index.handleWidgetDelete'
			);
		} else
			setWidgets(
				[...widgets.slice(0, indexToRemove), ...widgets.slice(indexToRemove + 1)],
				'index.handleWidgetDelete'
			);
	};

	const handleWidgetUpdate = (widget, index) => {
		setWidgetToUpdate({ ...widget, index });
		const widgetsWithNoOptions = ['Spacer'];
		if (!widgetsWithNoOptions.includes(widget.type)) setShowUpdateOptions(true);
	};

	const handleUpdate = propValues => {
		const { index, id } = widgetToUpdate;
		const widget = {
			...widgetToUpdate,
			props: { ...widgetToUpdate.props, ...propValues },
			groups: widgetToUpdate.groups,
			toUpdate: id ? true : false,
		};

		// changing back to source.src --> to be deleted
		if (widget.props.source && widget.props.source.uri) {
			widget.props.source.src = widget.props.source.uri;
			delete widget.props.source.uri;
		}

		setWidgetToUpdate(widget);
		setWidgets([...widgets.slice(0, index), widget, ...widgets.slice(index + 1)]);
		setHasChanges(true);
		setShowUpdateOptions(false);
	};

	const handleClose = () => {
		setWidgetToUpdate({});
		setShowUpdateOptions(false);
	};

	const handleUnsavedChangesWarning = e => {
		//temp fix: only show warning if mouse enters side nav classNames
		const classNames = ['header-content left', 'hover-wrap', 'WidgetBuilderHeader'];
		if (hasChanges && classNames.includes(e.target.className)) {
			notification.config({
				maxCount: 1,
			});
			notification.warning({
				message: 'Warning',
				description: 'You have unsaved changes',
				duration: 2,
			});
		}
	};

	return (
		<div className="WidgetBuilderContainer" onMouseLeave={e => handleUnsavedChangesWarning(e)}>
			<WidgetBuilderHeader
				page={page}
				handleSubmit={handleSubmit}
				event={event}
				preview={preview}
				setPreview={setPreview}
				hasChanges={hasChanges}
			/>
			<WidgetBuilder
				{...props}
				handleWidgetUpdate={handleWidgetUpdate}
				handleWidgetDelete={handleWidgetDelete}
				handleSubmit={handleSubmit}
				setWidgets={setWidgets}
				theme={theme}
				setTheme={setTheme}
				setHasChanges={setHasChanges}
				preview={preview}
				showUpdateOptions={showUpdateOptions}
				setShowUpdateOptions={setShowUpdateOptions}
				onUpdate={handleUpdate}
				widgetToUpdate={widgetToUpdate}
				setWidgetToUpdate={setWidgetToUpdate}
				handleClose={handleClose}
				pageName={pageName}
				setPageName={setPageName}
			/>
		</div>
	);
};

const msp = ({ auth, widgets }) => ({
	auth0Token: auth.auth0Token,
	widgets,
});
const mdp = dispatch => ({
	fetchHomescreenWidgets: (eventID, from) => dispatch(thunkFetchHomescreenWidgets(eventID, from)),
	setWidgets: (widgets, from) => dispatch(setWidgets(widgets, from)),
});

export default connect(msp, mdp)(WidgetBuilderContainer);
